import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { DASHBOARD_PERMISSION_ROUTE_ORDER } from '../constants/dashboardAccess';

const AuthContext = createContext();

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return [...new Set(value.filter((item) => typeof item === 'string' && item.trim()))];
};

const normalizeRoleValue = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : value;

const hasAdminRole = (userData = {}, tokenClaims = {}) => {
  const roles = userData.roles;
  const role = normalizeRoleValue(userData.role);
  const activeRole = normalizeRoleValue(userData.activeRole);

  return Boolean(
    roles?.admin === true ||
      (Array.isArray(roles) && roles.map(normalizeRoleValue).includes('admin')) ||
      role === 'admin' ||
      activeRole === 'admin' ||
      userData.admin === true ||
      userData.isAdmin === true ||
      userData.superAdmin === true ||
      userData.isSuperAdmin === true ||
      tokenClaims.admin === true ||
      tokenClaims.isAdmin === true ||
      tokenClaims.role === 'admin' ||
      tokenClaims.activeRole === 'admin'
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [accessMode, setAccessMode] = useState('full');
  const [assignedTeamIds, setAssignedTeamIds] = useState([]);
  const [dashboardPermissions, setDashboardPermissions] = useState([]);

  // Login function
  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Check if user is admin and approval status
  const checkAdminRole = async (user) => {
    if (!user) {
      setIsAdmin(false);
      setUserRole(null);
      setIsApproved(true);
      setApprovalStatus(null);
      setAccessMode('full');
      setAssignedTeamIds([]);
      setDashboardPermissions([]);
      return false;
    }

    try {
      const tokenResult = await user.getIdTokenResult?.().catch(() => null);
      const tokenClaims = tokenResult?.claims || {};
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check multiple possible admin indicators used across app, web, dashboard, and claims.
        const adminStatus = hasAdminRole(userData, tokenClaims);
        
        // Check approval status for clients
        const userApprovalStatus = userData.approvalStatus || 'approved';
        const isClientPending = userData.roles?.client === true && userApprovalStatus === 'pending';
        const isClientRejected = userData.roles?.client === true && userApprovalStatus === 'rejected';

        const adminAccess = userData.adminAccess || {};
        const resolvedAccessMode = adminAccess?.mode === 'restricted' ? 'restricted' : 'full';
        const resolvedTeamIds = normalizeStringArray(adminAccess?.teamIds);
        const directPermissions = normalizeStringArray(adminAccess?.directPermissions);

        let effectivePermissions = [];
        if (adminStatus && resolvedAccessMode === 'restricted') {
          const permissionSet = new Set(directPermissions);

          await Promise.all(
            resolvedTeamIds.map(async (teamId) => {
              try {
                const teamDoc = await getDoc(doc(db, 'adminTeams', teamId));
                if (!teamDoc.exists()) {
                  return;
                }

                const teamData = teamDoc.data() || {};
                if (teamData.isActive === false) {
                  return;
                }

                normalizeStringArray(teamData.permissions).forEach((permission) => {
                  permissionSet.add(permission);
                });
              } catch (teamError) {
                console.error(`Failed to load admin team ${teamId}:`, teamError);
              }
            })
          );

          effectivePermissions = Array.from(permissionSet);
        }
        
        setIsAdmin(adminStatus);
        setUserRole(userData.roles || {});
        setApprovalStatus(userApprovalStatus);
        setIsApproved(!isClientPending && !isClientRejected);
        if (adminStatus) {
          setAccessMode(resolvedAccessMode);
          setAssignedTeamIds(resolvedTeamIds);
          setDashboardPermissions(effectivePermissions);
        } else {
          setAccessMode('full');
          setAssignedTeamIds([]);
          setDashboardPermissions([]);
        }
        
        return adminStatus;
      } else {
        const adminStatus = hasAdminRole({}, tokenClaims);
        setIsAdmin(adminStatus);
        setUserRole(adminStatus ? { admin: true } : null);
        setIsApproved(true);
        setApprovalStatus(null);
        setAccessMode('full');
        setAssignedTeamIds([]);
        setDashboardPermissions([]);
        return adminStatus;
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
      setIsAdmin(false);
      setUserRole(null);
      setIsApproved(true);
      setApprovalStatus(null);
      setAccessMode('full');
      setAssignedTeamIds([]);
      setDashboardPermissions([]);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await checkAdminRole(user);
      } else {
        setIsAdmin(false);
        setUserRole(null);
        setIsApproved(true);
        setApprovalStatus(null);
        setAccessMode('full');
        setAssignedTeamIds([]);
        setDashboardPermissions([]);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!isAdmin) {
      return false;
    }

    if (!permission) {
      return true;
    }

    if (accessMode !== 'restricted') {
      return true;
    }

    return dashboardPermissions.includes(permission);
  }, [isAdmin, accessMode, dashboardPermissions]);

  const getFirstAllowedDashboardPath = useCallback(() => {
    if (!isAdmin) {
      return '/login';
    }

    if (accessMode !== 'restricted') {
      return '/dashboard';
    }

    const firstAllowedRoute = DASHBOARD_PERMISSION_ROUTE_ORDER.find(({ permission }) =>
      dashboardPermissions.includes(permission)
    );

    return firstAllowedRoute?.path || '/dashboard';
  }, [isAdmin, accessMode, dashboardPermissions]);

  const refreshAccessControl = async () => {
    if (currentUser) {
      await checkAdminRole(currentUser);
    }
  };

  const value = {
    currentUser,
    userRole,
    isAdmin,
    isApproved,
    approvalStatus,
    accessMode,
    assignedTeamIds,
    dashboardPermissions,
    hasPermission,
    getFirstAllowedDashboardPath,
    refreshAccessControl,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
